import {gql} from '@apollo/client';
import {groupByUniqueKey} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import produce from 'immer';
import React, {useCallback, useMemo} from 'react';

import {useSkippableBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {ClientId} from 'notebook/ELNConstants';
import {isMixtureGroup} from 'notebook/integrations/registry/MixtureGroupConstants';
import {
  AugmentNotePartsContext,
  updateTableNotePartsFromTableMap,
} from 'notebook/mediocre/core-ui/augmentNoteParts';
import {getMixtureGroupAdapter} from 'notebook/notes/groups/NoteLinkedGroupAdapters';
import getNotePartType from 'notebook/notes/util/getNotePartType';
import OptionSearchCellDropdown from 'notebook/plain-tables/selectors/OptionSearchCellDropdown';
import toColumnWidth from 'notebook/structured-tables/columns/toColumnWidth';
import {
  StructuredTableColumn,
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {extractTables} from 'notebook/tables/augmentNotePartTablesWithValues';
import {InsertableColumnInfo, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {
  deleteColumns,
  insertColumnsLeft,
  insertColumnsRight,
  wrapTableOpForSingleDoc,
} from 'notebook/tables/excess/ops/TableOps';
import TableUtil from 'notebook/tables/TableUtil';
import {
  ProcedureConditionReplicate,
  ProcedureConditionReplicatesTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {
  GenericStructuredTable,
  NoteLinkedObject,
  NoteLinkedObjectType,
  NotePart,
} from 'notebook/util/NoteUtilConstants';
import {
  ProcedureMethodConditionReplicatesForDocumentQuery,
  ProcedureMethodConditionReplicatesForDocumentQueryVariables,
} from 'procedures/tables/__generated/ProcedureMethodConditionReplicatesForDocumentQuery';
import {ERROR_OPTION_DATA, LOADING_OPTION_DATA, OptionData} from 'ui-library/menus/MenuConstants';
import APIUtil from 'util/APIUtil';

export interface ProcedureConditionReplicateCellDisplayProps {
  conditionName: string;
  replicateNumber: number;
}

export function ProcedureConditionReplicateCellDisplay({
  conditionName,
  replicateNumber,
}: ProcedureConditionReplicateCellDisplayProps): JSX.Element {
  return (
    <div className={css(styles.procedureConditionReplicateCellDisplay)}>
      {/* Add a tooltip to the Condition name since it can often become ellipsized */}
      <span className={css(styles.conditionName)} title={conditionName}>
        {conditionName}
      </span>
      <span className={css(styles.replicateNumber)}>{replicateNumber}</span>
    </div>
  );
}

const styles = StyleSheet.create({
  procedureConditionReplicateCellDisplay: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  conditionName: {
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  replicateNumber: {
    flex: 0,
    marginLeft: 8,
    color: Color.GRAY_600,
  },
});

export const PROCEDURE_METHOD_CONDITION_REPLICATES_FOR_DOCUMENT_QUERY = gql`
  query ProcedureMethodConditionReplicatesForDocumentQuery($documentId: StrictString)
  @skipAudit
  @engComponent(name: DATA_INPUT) {
    procedureStepExecutionInstances(documentId: $documentId) {
      ... on ProcedureStepExecutionInstanceNode {
        id
        archiveData {
          archived
        }
        methodExecutionInstance {
          id
          procedureRunConditionReplicate {
            id
            replicateNumber
            procedureRunCondition {
              id
              name
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ProcedureConditionReplicateCellEditorProps {
  fileId: string;
  typedValue: ProcedureConditionReplicatesTypedValue | null;
  onUpdate: (typedValue: ProcedureConditionReplicatesTypedValue | null) => void;
  // This will typically be rendered inside a table cell. If that's not the case, set this to true
  isOutsideTable?: boolean;
}

/** A selector component for choosing from a list of Condition-Replicates.
 *
 * Fetches the Condition-Replicates based on the document.
 */
export function ProcedureConditionReplicateCellEditor(
  props: ProcedureConditionReplicateCellEditorProps
): JSX.Element {
  const {fileId, typedValue, onUpdate} = props;
  const isInDocument = APIUtil.isDocument(fileId);
  const procedureConditionReplicatesQueryResult = useSkippableBenchlingQuery<
    ProcedureMethodConditionReplicatesForDocumentQuery,
    ProcedureMethodConditionReplicatesForDocumentQueryVariables
  >(PROCEDURE_METHOD_CONDITION_REPLICATES_FOR_DOCUMENT_QUERY, {
    variables: {documentId: fileId},
    // A Condition-Replicate archived after this list loads stays selectable until reload; the server rejects new
    // rows that reference it.
    fetchPolicy: 'cache-first',
    skip: !isInDocument,
  });
  const selectedConditionReplicate: ProcedureConditionReplicate | null = useMemo(
    () => typedValue?.conditionReplicates?.[0] ?? null,
    [typedValue]
  );

  const conditionReplicates: Array<ProcedureConditionReplicate> = useMemo(() => {
    if (procedureConditionReplicatesQueryResult.type !== QueryResultType.SUCCESS) {
      return [];
    }
    return accessibleObjs(procedureConditionReplicatesQueryResult.data.procedureStepExecutionInstances)
      .filter((psei) => !psei.archiveData.archived)
      .map((psei) => {
        const pmei = psei.methodExecutionInstance;
        const conditionReplicate = pmei.procedureRunConditionReplicate;
        return {
          procedureMethodExecutionInstanceId: pmei.id,
          conditionName: conditionReplicate.procedureRunCondition.name,
          replicateNumber: conditionReplicate.replicateNumber,
        };
      });
  }, [procedureConditionReplicatesQueryResult]);
  const conditionReplicatesByPMEI: Record<string, ProcedureConditionReplicate> = useMemo(
    () => groupByUniqueKey(conditionReplicates, (condRepl) => condRepl.procedureMethodExecutionInstanceId),
    [conditionReplicates]
  );
  const selectionOptions: Array<OptionData> = useMemo(() => {
    switch (procedureConditionReplicatesQueryResult.type) {
      case QueryResultType.SUCCESS:
        return conditionReplicates.map((conditionReplicate): OptionData => ({
          name: (
            <ProcedureConditionReplicateCellDisplay
              conditionName={conditionReplicate.conditionName}
              replicateNumber={conditionReplicate.replicateNumber}
            />
          ),
          value: conditionReplicate.procedureMethodExecutionInstanceId,
          pureTextName: `${conditionReplicate.conditionName} Replicate ${conditionReplicate.replicateNumber}`,
        }));
      case QueryResultType.PENDING: {
        return [LOADING_OPTION_DATA];
      }
      case QueryResultType.SKIPPED:
      case QueryResultType.ERROR:
      default: {
        const errorOption: OptionData = {
          ...ERROR_OPTION_DATA,
          name: 'Error. Unable to load Condition-Replicate options',
        };
        return [errorOption];
      }
    }
  }, [conditionReplicates, procedureConditionReplicatesQueryResult.type]);

  const onUpdateOptionSearchCellDropdown = useCallback(
    (selectedOptionId: string) => {
      if (!selectedOptionId) {
        onUpdate(null);
        return;
      }
      const selectedConditionReplicate = conditionReplicatesByPMEI[selectedOptionId] ?? null;
      if (!selectedConditionReplicate) {
        onUpdate(null);
        return;
      }
      const typedValue: ProcedureConditionReplicatesTypedValue = {
        type: TableColumnType.PROCEDURE_CONDITION_REPLICATES,
        conditionReplicates: [selectedConditionReplicate],
      };
      onUpdate(typedValue);
    },
    [conditionReplicatesByPMEI, onUpdate]
  );

  return (
    <OptionSearchCellDropdown
      isSelected={!props.isOutsideTable}
      options={selectionOptions}
      onUpdate={(selectedOptionId) => {
        onUpdateOptionSearchCellDropdown(selectedOptionId);
      }}
      fullWidth={true}
      staticDisplay={
        selectedConditionReplicate && (
          <ProcedureConditionReplicateCellDisplay
            conditionName={selectedConditionReplicate.conditionName}
            replicateNumber={selectedConditionReplicate.replicateNumber}
          />
        )
      }
    />
  );
}

export const REQUIRED_PROCEDURE_CONDITION_REPLICATE_STRUCTURED_TABLE_COLUMN: StructuredTableColumn =
  makeStructuredTableColumn({
    columnType: TableColumnType.PROCEDURE_CONDITION_REPLICATES,
    name: 'Condition - Replicate',
    isRequired: true,
  });

/**
 * Some tables have optional Condition-Replicate columns because their values aren't actually used to
 * set the Condition-Replicate on the backend. Usually this is tables that only *update* an object rather than
 * tables that create objects.
 */
export const OPTIONAL_PROCEDURE_CONDITION_REPLICATE_STRUCTURED_TABLE_COLUMN: StructuredTableColumn = produce(
  REQUIRED_PROCEDURE_CONDITION_REPLICATE_STRUCTURED_TABLE_COLUMN,
  (draft) => {
    draft.isRequired = false;
  }
);

type InsertOrRemoveProcedureConditionReplicateColumnContext = Pick<
  AugmentNotePartsContext,
  'fileId' | 'isProcedureMultibatchExecution'
>;

/** For all inserted StructuredTables in a given set of NoteParts, insert a Condition-Replicate column
 * if we're in Procedure Multi-Batch Execution.
 *
 * If we're not in Multi-Batch, remove a Condition-Replicate column if one happens to exist (like when
 * copying tables from Multi-Batch to somewhere else).
 *
 * We must do this with access to all Note Parts because we want to insert the column into the front of the
 * table which could shift formulas in pasted tables and subtemplates. This function also updates mixture
 * metadata tables, but not mixture ingredients tables.
 */
export function insertOrRemoveProcedureConditionReplicateColumn(
  noteParts: Array<NotePart>,
  {fileId, isProcedureMultibatchExecution}: InsertOrRemoveProcedureConditionReplicateColumnContext
): Array<NotePart> {
  let {fullTableMap: tableMap} = extractTables(noteParts, fileId, [], {includeNoteLinkedGroups: true});

  for (const notePart of noteParts) {
    let structuredTable: UnknownStructuredTable;

    const notePartType = getNotePartType(notePart);
    if (notePartType === NoteLinkedObjectType.NOTE_LINKED_GROUP) {
      // Update the mixture metadata table in a mixture prep group, but not the ingredients table
      const mixtureGroup = notePart.item as NoteLinkedObject;
      if (!isMixtureGroup(mixtureGroup)) {
        continue;
      }
      structuredTable = getMixtureGroupAdapter().getMixtureMetadataStructuredTable(mixtureGroup);
    } else if (notePartType === NoteLinkedObjectType.STRUCTURED_TABLE) {
      // Update the structured table
      assert(notePart.item, 'StructuredTable NotePart must have an item');
      structuredTable = notePart.item as GenericStructuredTable<ClientId>;
    } else {
      // Skip non-structured tables
      continue;
    }

    const table = tableMap[structuredTable.name];
    const existingConditionReplicateColumnIndex = table.columnConstraints.findIndex(
      (columnConstraint) =>
        columnConstraint?.structuredTableColumn?.columnType === TableColumnType.PROCEDURE_CONDITION_REPLICATES
    );
    const singleInputBoundaryIndex = TableUtil.findSingleInputBoundaryIndex(table);
    const hasExistingConditionReplicateColumn = existingConditionReplicateColumnIndex !== -1;
    if (isProcedureMultibatchExecution && !hasExistingConditionReplicateColumn) {
      const conditionReplicateColumnToInsert = getConditionReplicateColumnForTable(structuredTable);
      if (conditionReplicateColumnToInsert) {
        const insertableColumnInfo: InsertableColumnInfo = {
          columnLabels: conditionReplicateColumnToInsert.name,
          columnConstraints: {
            structuredTableColumn: conditionReplicateColumnToInsert,
          },
          widths: toColumnWidth({columnType: TableColumnType.PROCEDURE_CONDITION_REPLICATES}),
        };
        if (singleInputBoundaryIndex === 0) {
          // No single inputs, so insert at the front
          tableMap = wrapTableOpForSingleDoc((targetTable, documentName) =>
            insertColumnsLeft(targetTable, documentName, 0, 0, insertableColumnInfo)
          )(tableMap, {}, table.name).tables;
        } else {
          // There is at least one single input, so insert after the last single input column
          tableMap = wrapTableOpForSingleDoc((targetTable, documentName) =>
            insertColumnsRight(
              targetTable,
              documentName,
              singleInputBoundaryIndex - 1,
              singleInputBoundaryIndex - 1,
              insertableColumnInfo
            )
          )(tableMap, {}, table.name).tables;
        }
      }
    } else if (!isProcedureMultibatchExecution && hasExistingConditionReplicateColumn) {
      // If not in Multi-Batch Execution, remove the Condition-Replicate column if one exists
      tableMap = wrapTableOpForSingleDoc((targetTable, documentName) =>
        deleteColumns(
          targetTable,
          documentName,
          existingConditionReplicateColumnIndex,
          existingConditionReplicateColumnIndex
        )
      )(tableMap, {}, table.name).tables;
    }
  }

  return updateTableNotePartsFromTableMap(tableMap, noteParts);
}

/**
 * @package private
 * KEEP IN_SYNC WITH benchling/procedures/integrations/data_input/procedure_condition_replicates/tables/procedure_condition_replicates_column.py#make_procedure_condition_replicates_column
 * This function shouldn't need updating, but if you do, ensure that tables compiled on the backend follow the
 * same logic.
 *
 * Returns a Condition-Replicate column for a given StructuredTable or null if the table should not have a
 * Condition-Replicate column.
 */
export function getConditionReplicateColumnForTable(
  structuredTable: UnknownStructuredTable
): StructuredTableColumn | null {
  switch (structuredTable.kind) {
    case StructuredTableKind.PARAMETER: {
      return REQUIRED_PROCEDURE_CONDITION_REPLICATE_STRUCTURED_TABLE_COLUMN;
    }
    case StructuredTableKind.BOX_CREATION:
    case StructuredTableKind.MIXTURE_METADATA:
    case StructuredTableKind.PLATE_CREATION:
    case StructuredTableKind.RESULTS:
    case StructuredTableKind.INVENTORY:
    case StructuredTableKind.LOOKUP:
    case StructuredTableKind.REGISTRATION:
    case StructuredTableKind.TRANSFER: {
      return OPTIONAL_PROCEDURE_CONDITION_REPLICATE_STRUCTURED_TABLE_COLUMN;
    }
    default:
      return null;
  }
}
